import { useSelector } from 'react-redux';
import {
  selectCurrentImageDocumentContextKey,
  selectImagesDocuments,
  updateImageDocumentInDict,
} from '../../../../reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useEffect, useMemo, useState } from 'react';
import { FavoriteButtonProps } from '@components/common/favoriteButton';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { handleToggleFavoriteLink, updateImageMetadata } from '@reducers/v2/imageHandlingV2/thunks';
import { navigationRef } from '@helpers';

interface UseManageFavoriteButtonParams extends Pick<FavoriteButtonProps, 'scopeId' | 'scopeReferenceId'> {
  contextKey?: string | null;
}

export const useManageFavoriteButton = ({ scopeId, scopeReferenceId, contextKey }: UseManageFavoriteButtonParams) => {
  const route = navigationRef?.current?.getCurrentRoute();
  const dispatch = useAppDispatch();
  const routeParams = route?.params;

  const fallbackContextKey = useSelector(selectCurrentImageDocumentContextKey);
  // `undefined` means "no override given" — use the slice's current key. `null` is a valid
  // explicit opt-out (caller knows there is no context).
  const effectiveContextKey = contextKey !== undefined ? contextKey : fallbackContextKey;

  const selectImages = useMemo(() => selectImagesDocuments(effectiveContextKey), [effectiveContextKey]);
  const images = useSelector(selectImages);

  const [isFavorite, setIsFavorite] = useState<boolean>(false);

  const selectedImageKey = routeParams?.selectedImageKey;

  const selectedImage = images.length === 1 ? images[0] : images?.find((i) => i.contentHash === selectedImageKey);

  const isAlreadyAttachedIndex =
    selectedImage && selectedImage?.links
      ? selectedImage?.links?.findIndex((l) => l?.scopeId === scopeId && l?.scopeReferenceId === scopeReferenceId)
      : -1;

  const isAlreadyAttached = isAlreadyAttachedIndex >= 0;

  const currentlyIsFavorite = isAlreadyAttached && !!selectedImage?.links?.[isAlreadyAttachedIndex]?.isFavorite;

  useEffect(() => {
    setIsFavorite(currentlyIsFavorite);
  }, [currentlyIsFavorite]);

  const onButtonPress = async () => {
    if (!selectedImage || selectedImage?.documentId == null || scopeId == null || scopeReferenceId == null) return;

    setIsFavorite((prev) => !prev);

    if (isAlreadyAttached) {
      await dispatch(handleToggleFavoriteLink({ img: selectedImage, scopeId, scopeReferenceId }));
      return;
    }

    const newLink = {
      scopeId,
      scopeReferenceId,
      isFavorite: !isFavorite,
      isActivated: true,
      isFavoriteChanged: true,
    };

    const attachmentData = {
      documentId: selectedImage.documentId as number,
      documentLinks: [
        {
          scopeId,
          scopeReferenceId,
          isFavorite: !isFavorite,
          isActivated: true,
          isFavoriteChanged: true,
        },
      ],
      documentTags: [],
    };

    dispatch(
      updateImageDocumentInDict({
        documentId: selectedImage.documentId,
        updatedImageDocument: { ...selectedImage, links: [...(selectedImage.links ?? []), newLink] },
      })
    );
    await dispatch(updateImageMetadata({ data: attachmentData }));
  };

  return { onButtonPress, isFavorite };
};
